import { DateTime, Duration } from 'luxon';
import { rrulestr, RRule } from 'rrule';
import { ParsedRecurringSchedule } from '../types';

/**
 * Parses an ISO 8601 duration string into a Luxon Duration object
 */
export const parseDuration = (durationString: string): Duration => {
  if (!durationString) {
    throw new Error('Duration string is required');
  }

  const duration = Duration.fromISO(durationString);

  if (!duration.isValid) {
    throw new Error(`Invalid duration string: ${durationString}`);
  }

  return duration;
};

/**
 * Parses an RRule schedule string and returns formatted schedule information
 */
export const parseRRuleSchedule = (
  scheduleString: string,
  scheduleDuration: string
): ParsedRecurringSchedule => {
  let rrule: RRule;

  try {
    rrule = rrulestr(scheduleString);
  } catch {
    throw new Error(`Invalid RRule string: ${scheduleString}`);
  }

  const duration = parseDuration(scheduleDuration);
  const scheduleEndDate = rrule.options.until;

  // Get all occurrences from the RRule
  const allOccurrences = rrule.all();

  if (allOccurrences.length === 0) {
    throw new Error('No occurrences found in the schedule');
  }

  // Filter occurrences to only include those where the entire event fits within the schedule
  const validOccurrences = getValidOccurrences(
    allOccurrences,
    duration,
    scheduleEndDate
  );

  if (validOccurrences.length === 0) {
    throw new Error(
      'No valid occurrences found - all events would extend beyond the schedule end date'
    );
  }

  const firstOccurrence = validOccurrences[0];
  const lastOccurrence = validOccurrences[validOccurrences.length - 1];

  // Generate human-readable schedule text
  const scheduleText = generateScheduleText(rrule);

  // Calculate the end time of the first event
  const firstEventEndTime = DateTime.fromJSDate(firstOccurrence)
    .plus(duration)
    .toISO();

  return {
    scheduleText,
    scheduleStartDatetime: firstOccurrence.toISOString(),
    scheduleEndDate: lastOccurrence.toISOString(),
    eventEndTime: firstEventEndTime,
  };
};

/**
 * Filters occurrences to only include those where the entire event fits within the schedule
 */
function getValidOccurrences(
  occurrences: Date[],
  eventDuration: Duration,
  scheduleEndDate: Date | null
): Date[] {
  // If there's no end date constraint, all occurrences are valid
  if (!scheduleEndDate) {
    return occurrences;
  }

  const scheduleEndTimestamp = scheduleEndDate.getTime();
  const eventDurationMs = eventDuration.toMillis();

  return occurrences.filter(occurrence => {
    const eventEndTimestamp = occurrence.getTime() + eventDurationMs;

    return eventEndTimestamp <= scheduleEndTimestamp;
  });
}

/**
 * Generates a human-readable schedule description from an RRule
 */
function generateScheduleText(rrule: RRule): string {
  const ruleText = rrule.toText();

  // Remove the part after "until" from the text since we handle date ranges separately
  const textWithoutUntil = ruleText.split(' until ')[0];

  // Add "between" to indicate this is a date range
  return `${textWithoutUntil} between`;
}
